import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableRow, TableHead, Checkbox } from '@material-ui/core';
import { parseDate } from 'components/business/consents/ConsentsTable';
import { useAlerts } from 'hooks/alerts-hooks';
import axiosClient from 'http/axios-client';
import CollectionKeys from 'hooks/molecules/collection-keys';

const useStyles = makeStyles(() => {
  return {
    dialogPaper: {
      width: '100%',
      maxWidth: 1200,
    },
    dialogTitle: {
      padding: '16px 24px',
      borderBottom: '1px solid #e0e0e0',
    },
    dialogContent: {
      padding: 0,
    },
    table: {
      minWidth: 650,
    },
    header: {
      backgroundColor: '#f5f5f5',
    },
    headerCell: {
      fontWeight: 'bold',
    },
    row: {
      '&:nth-of-type(odd)': {
        backgroundColor: '#fafafa',
      },
      '&:hover': {
        backgroundColor: '#f0f0f0',
      },
    },
    cell: {
      padding: '12px 16px',
    },
    button: {
      backgroundColor: '#d0d2d3ff',
    },
    greenButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(7, 177, 44, 1)',
      '&:hover': {
        backgroundColor: '#033a11ff',
      },
    },
  };
});

function LabFilesDialog({ selectedFile, toImport, setToImport }) {
  const { alertError } = useAlerts();
  const [ numberOfDuplicate, setNumberOfDuplicate ] = useState(0);
  const [ loading, setLoading ] = useState(false);
  const [ noRegister, setNoRegister ] = useState([]);
  const classes = useStyles();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const results = await Promise.all(
        selectedFile.metadata.map(async (item) => {
          const count = await axiosClient(
            `${ CollectionKeys.REGISTRATION_KITS }/count/?serial_number_id.serial=${ item?.sample_name }`,
          );

          return count === 0 ? item?.uid : null;
        }),
      );

      const filtered = results.filter(Boolean);
      setNoRegister(filtered);
      setLoading(false);
    };
    if (selectedFile?.metadata?.length) {
      fetchData();
    }
  }, [ selectedFile ]);

  function extractTime(dateStr) {
    return dateStr.split('T')[1].substring(0, 8);
  }

  function handleChange(e, itemSelected) {
    if (e.target.checked) {
      // check if already exist the same serial
      const hasDuplicate = toImport.some(
        (item) => { return item?.sample_name === itemSelected?.sample_name; },
      );
      if (hasDuplicate) {
        alertError(`Serial ${ itemSelected?.sample_name } already selected. This selection will not be imported.`);
      } else {
        setToImport([ ...toImport, itemSelected ]);
      }
    } else {
      // if not checked
      const filtered = toImport.filter((item) => { return item?.uid !== itemSelected?.uid; });
      setToImport(filtered);
    }
  }

  function handleSelectAllClick(e) {
    setToImport([]);
    setNumberOfDuplicate(0);
    const seen = new Set();
    const fnl = (selectedFile?.metadata || []).filter((item) => {
      if (seen.has(item.sample_name)) { return false; }
      seen.add(item.sample_name);
      return true;
    });
    if (e.target.checked) {
      setToImport(fnl);
      setNumberOfDuplicate(selectedFile?.metadata.length - fnl.length);
    }
  }

  return (
    <>
      <span style={ { padding: '10px' } }>
        File Name:
        {' '}
        <b>{selectedFile?.file_name}</b>
      </span>
      {undefined !== selectedFile?.metadata && Array.isArray(selectedFile?.metadata)
                && (
                  <>
                    <span style={ { padding: '10px' } }>
                      File Lines:
                      {' '}
                      <b>{selectedFile?.metadata?.length}</b>
                    </span>
                    <span style={ { padding: '2px', color: '#7e0303' } }>
                      No Kit Registration :
                      {' '}
                      <b>{loading ? 'LOADING...' : noRegister?.length}</b>
                    </span>
                    <Table className={ classes.table }>
                      <TableHead className={ classes.header }>
                        <TableCell className={ classes.headerCell }>
                          <Checkbox
                            indeterminate={
                              toImport.length > 0 && toImport.length < selectedFile?.metadata.length - numberOfDuplicate
                            }
                            checked={
                              selectedFile?.metadata.length > 0 && toImport.length === selectedFile?.metadata.length - numberOfDuplicate
                            }
                            onChange={ (e) => { handleSelectAllClick(e); } }
                          />
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Sample Serial
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Date
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Time
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Nucleic Acid
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          A260/A280
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          A260/A230
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          A260
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          A280
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Nucleic Acid Factor
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Baseline Correction
                        </TableCell>
                        <TableCell className={ classes.headerCell }>
                          Baseline Absorbance
                        </TableCell>

                      </TableHead>
                      <TableBody>
                        {selectedFile?.metadata?.slice()
                          .sort((a, b) => {
                            return (a?.sample_name || '')
                              .localeCompare(b?.sample_name || '');
                          })
                          .map((item) => {
                            return (
                              <TableRow
                                key={ item?.uid }
                                className={ classes.row }
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={ toImport.some((imported) => { return imported?.uid === item?.uid; }) }
                                    onChange={ (e) => { handleChange(e, item); } }
                                  />
                                </TableCell>
                                <TableCell
                                  className={ classes.cell }
                                  style={ noRegister.includes(item?.uid) ? { color: '#7e0303' } : { color: '#000' } }
                                >
                                  {item?.sample_name}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {parseDate(item?.date)}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {extractTime(item?.date)}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {item?.nucleic_acid}
                                </TableCell>
                                <TableCell>
                                  {item?.a260_a280}
                                </TableCell>
                                <TableCell>
                                  {item?.a260_a230}
                                </TableCell>
                                <TableCell>
                                  {item?.a260}
                                </TableCell>
                                <TableCell>
                                  {item?.a280}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {item?.nucleic_acid_factor}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {item?.baseline_correction}
                                </TableCell>
                                <TableCell className={ classes.cell }>
                                  {item?.baseline_absorbance}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </>
                )}
    </>
  );
}

export default LabFilesDialog;
